import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Shield, ArrowLeft, Users, FileText, MessageSquare,
  TrendingUp, CheckCircle, XCircle, Trash2, Clock,
  BarChart3, Eye, Share2, Copy, ExternalLink, Mail
} from 'lucide-react';
import axios from 'axios';
import AppFooter from '../components/AppFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [testimonies, setTestimonies] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [ministryCode, setMinistryCode] = useState(null);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [waitlist, setWaitlist] = useState(null);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      const [statsRes, usersRes, testRes, disputeRes, waitRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`),
        axios.get(`${API_URL}/api/admin/users`),
        axios.get(`${API_URL}/api/admin/testimonies`),
        axios.get(`${API_URL}/api/admin/disputes`),
        axios.get(`${API_URL}/api/admin/waitlist`)
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setTestimonies(testRes.data);
      setDisputes(disputeRes.data);
      setWaitlist(waitRes.data);
      
      // Load ministry code
      try {
        const { data: codeData } = await axios.get(`${API_URL}/api/admin/ministry-code`);
        if (codeData.code) setMinistryCode(codeData);
      } catch (codeErr) {
        // Ministry code endpoint may 404 if no code generated yet — non-fatal
        console.debug('[Admin] No ministry code yet:', codeErr?.message);
      }
    } catch (e) {
      console.error('Failed to load admin data:', e);
      if (e.response?.status === 403) navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const approveTestimony = async (id) => {
    try {
      await axios.put(`${API_URL}/api/admin/testimonies/${id}/approve`);
      toast.success('Testimony approved');
      loadData();
    } catch (e) { toast.error('Failed to approve'); }
  };

  const rejectTestimony = async (id) => {
    try {
      await axios.put(`${API_URL}/api/admin/testimonies/${id}/reject`);
      toast.success('Testimony hidden');
      loadData();
    } catch (e) { toast.error('Failed to reject'); }
  };

  const deleteTestimony = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/admin/testimonies/${id}`);
      toast.success('Testimony deleted');
      loadData();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const generateMinistryCode = async () => {
    setGeneratingCode(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/ministry-code`);
      setMinistryCode(data);
      toast.success('Ministry dashboard link created!');
    } catch (e) { toast.error('Failed to generate code'); }
    finally { setGeneratingCode(false); }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#B76E79] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="admin-page">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Shield className="w-8 h-8 text-[#B76E79]" strokeWidth={1.5} />
              <span className="ml-2 font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">Selah Slate&trade;</span>
              <span className="ml-3 px-2 py-0.5 bg-[#B76E79] text-white text-[10px] font-['Manrope'] uppercase tracking-wider rounded">Admin</span>
            </div>
            <div className="flex items-center space-x-3">
              <Link to="/dashboard" className="text-sm text-[#36454F]/60 hover:text-[#36454F] font-['Manrope']">Dashboard</Link>
              <Button variant="ghost" onClick={handleLogout} className="text-[#36454F]/70">Sign Out</Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/dashboard" className="inline-flex items-center text-[#36454F]/65 hover:text-[#36454F] font-['Manrope'] mb-6 transition-colors" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />Back to Dashboard
        </Link>

        <div className="mb-8">
          <h1 className="font-['Playfair_Display'] text-3xl font-semibold text-[#36454F]">Admin Panel</h1>
          <p className="mt-1 text-[#36454F]/65 font-['Manrope']">Manage testimonies, view analytics, and monitor user activity</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-[#36454F]/15 mb-6">
            <TabsTrigger value="overview" className="font-['Manrope'] data-[state=active]:bg-[#B76E79] data-[state=active]:text-white" data-testid="tab-overview">
              <BarChart3 className="w-4 h-4 mr-1.5" strokeWidth={1.5} />Overview
            </TabsTrigger>
            <TabsTrigger value="testimonies" className="font-['Manrope'] data-[state=active]:bg-[#B76E79] data-[state=active]:text-white" data-testid="tab-testimonies">
              <MessageSquare className="w-4 h-4 mr-1.5" strokeWidth={1.5} />Testimonies
              {stats?.testimonies?.pending_review > 0 && (
                <span className="ml-1.5 w-5 h-5 bg-[#D9534F] text-white text-[10px] rounded-full flex items-center justify-center">{stats.testimonies.pending_review}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="users" className="font-['Manrope'] data-[state=active]:bg-[#B76E79] data-[state=active]:text-white" data-testid="tab-users">
              <Users className="w-4 h-4 mr-1.5" strokeWidth={1.5} />Users
            </TabsTrigger>
            <TabsTrigger value="disputes" className="font-['Manrope'] data-[state=active]:bg-[#B76E79] data-[state=active]:text-white" data-testid="tab-disputes">
              <FileText className="w-4 h-4 mr-1.5" strokeWidth={1.5} />Disputes
            </TabsTrigger>
            <TabsTrigger value="ministry" className="font-['Manrope'] data-[state=active]:bg-[#B76E79] data-[state=active]:text-white" data-testid="tab-ministry">
              <Share2 className="w-4 h-4 mr-1.5" strokeWidth={1.5} />Ministry
            </TabsTrigger>
            <TabsTrigger value="waitlist" className="font-['Manrope'] data-[state=active]:bg-[#B76E79] data-[state=active]:text-white" data-testid="tab-waitlist">
              <Mail className="w-4 h-4 mr-1.5" strokeWidth={1.5} />Waitlist
              {waitlist?.total > 0 && (
                <span className="ml-1.5 w-5 h-5 bg-[#B76E79] text-white text-[10px] rounded-full flex items-center justify-center">{waitlist.total}</span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            {stats && (
              <div className="space-y-6 animate-fade-in-up">
                {/* Stat Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard icon={Users} label="Total Users" value={stats.users.total} sub={`+${stats.users.last_7_days} this week`} color="#B76E79" />
                  <StatCard icon={FileText} label="Total Disputes" value={stats.disputes.total} sub={`${stats.disputes.by_status.resolved || 0} resolved`} color="#4CAF50" />
                  <StatCard icon={TrendingUp} label="Uploads" value={stats.uploads} sub="Credit reports" color="#36454F" />
                  <StatCard icon={MessageSquare} label="Testimonies" value={stats.testimonies.total} sub={`${stats.testimonies.pending_review} pending`} color="#E0BFB8" />
                </div>

                {/* Dispute Status Breakdown */}
                <Card className="border-[#36454F]/15 bg-white">
                  <CardHeader>
                    <CardTitle className="font-['Playfair_Display'] text-lg text-[#36454F]">Dispute Pipeline</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                      {Object.entries(stats.disputes.by_status).map(([status, count]) => (
                        <div key={status} className="p-3 bg-[#F5F5F5] rounded-sm text-center">
                          <span className="text-2xl font-['Manrope'] font-semibold text-[#36454F]">{count}</span>
                          <p className="text-xs text-[#36454F]/50 font-['Manrope'] mt-1 capitalize">{status.replace('_', ' ')}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* User Growth */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="border-[#36454F]/15 bg-white">
                    <CardHeader>
                      <CardTitle className="font-['Playfair_Display'] text-lg text-[#36454F]">User Growth</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <GrowthBar label="Last 7 days" value={stats.users.last_7_days} max={stats.users.total} />
                      <GrowthBar label="Last 30 days" value={stats.users.last_30_days} max={stats.users.total} />
                      <GrowthBar label="All time" value={stats.users.total} max={stats.users.total} />
                    </CardContent>
                  </Card>

                  <Card className="border-[#36454F]/15 bg-white">
                    <CardHeader>
                      <CardTitle className="font-['Playfair_Display'] text-lg text-[#36454F]">Recent Disputes</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {stats.recent_disputes?.slice(0, 5).map((d, i) => (
                          <div key={i} className="flex items-center justify-between p-2 bg-[#F5F5F5] rounded-sm">
                            <span className="text-sm font-['Manrope'] text-[#36454F] capitalize">{d.bureau}</span>
                            <div className="flex items-center space-x-2">
                              <StatusBadge status={d.status} />
                              <span className="text-xs text-[#36454F]/40 font-['Manrope']">
                                {new Date(d.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Testimonies Tab */}
          <TabsContent value="testimonies">
            <div className="space-y-4 animate-fade-in-up">
              {testimonies.length === 0 ? (
                <Card className="border-[#36454F]/15 bg-white">
                  <CardContent className="p-12 text-center">
                    <MessageSquare className="w-12 h-12 text-[#36454F]/15 mx-auto mb-3" strokeWidth={1.5} />
                    <p className="text-[#36454F]/50 font-['Manrope']">No testimonies yet</p>
                  </CardContent>
                </Card>
              ) : (
                testimonies.map((t) => (
                  <Card key={t.testimony_id} className={`border bg-white ${!t.approved ? 'border-[#E0BFB8]' : 'border-[#36454F]/10'}`} data-testid={`admin-testimony-${t.testimony_id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="font-['Manrope'] font-medium text-[#36454F]">{t.user_name || 'Anonymous'}</span>
                            {t.approved ? (
                              <span className="text-[10px] px-1.5 py-0.5 bg-[#4CAF50]/10 text-[#4CAF50] font-['Manrope'] rounded">Approved</span>
                            ) : (
                              <span className="text-[10px] px-1.5 py-0.5 bg-[#E0BFB8]/40 text-[#B76E79] font-['Manrope'] rounded">Pending</span>
                            )}
                            <span className="text-xs text-[#36454F]/30 font-['Manrope']">
                              {new Date(t.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                          </div>
                          <p className="text-sm text-[#36454F]/80 font-['Manrope'] mb-2">{t.message}</p>
                          {t.scripture && (
                            <p className="text-xs text-[#36454F]/50 font-['Playfair_Display'] italic">
                              "{t.scripture}" {t.scripture_ref && `— ${t.scripture_ref}`}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center space-x-1 ml-4 flex-shrink-0">
                          {!t.approved && (
                            <button onClick={() => approveTestimony(t.testimony_id)} className="p-2 hover:bg-[#4CAF50]/10 rounded transition-colors" title="Approve" data-testid={`approve-${t.testimony_id}`}>
                              <CheckCircle className="w-5 h-5 text-[#4CAF50]" strokeWidth={1.5} />
                            </button>
                          )}
                          {t.approved && (
                            <button onClick={() => rejectTestimony(t.testimony_id)} className="p-2 hover:bg-[#E0BFB8]/20 rounded transition-colors" title="Hide" data-testid={`reject-${t.testimony_id}`}>
                              <XCircle className="w-5 h-5 text-[#B76E79]" strokeWidth={1.5} />
                            </button>
                          )}
                          <button onClick={() => deleteTestimony(t.testimony_id)} className="p-2 hover:bg-[#D9534F]/10 rounded transition-colors" title="Delete" data-testid={`delete-testimony-${t.testimony_id}`}>
                            <Trash2 className="w-5 h-5 text-[#D9534F]" strokeWidth={1.5} />
                          </button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="border-[#36454F]/15 bg-white animate-fade-in-up">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#36454F]/10 bg-[#F5F5F5]">
                        <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-[#36454F]/50 font-['Manrope'] font-medium">Name</th>
                        <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-[#36454F]/50 font-['Manrope'] font-medium">Email</th>
                        <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-[#36454F]/50 font-['Manrope'] font-medium">Role</th>
                        <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-[#36454F]/50 font-['Manrope'] font-medium">Disputes</th>
                        <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-[#36454F]/50 font-['Manrope'] font-medium">Uploads</th>
                        <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-[#36454F]/50 font-['Manrope'] font-medium">Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u, i) => (
                        <tr key={i} className="border-b border-[#36454F]/5 hover:bg-[#F5F5F5]/50 transition-colors" data-testid={`user-row-${i}`}>
                          <td className="py-3 px-4 text-sm font-['Manrope'] text-[#36454F] font-medium">{u.name || '—'}</td>
                          <td className="py-3 px-4 text-sm font-['Manrope'] text-[#36454F]/70">{u.email}</td>
                          <td className="py-3 px-4">
                            <span className={`text-xs px-2 py-0.5 rounded font-['Manrope'] ${u.role === 'admin' ? 'bg-[#B76E79]/10 text-[#B76E79]' : 'bg-[#36454F]/5 text-[#36454F]/60'}`}>
                              {u.role || 'user'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm font-['Manrope'] text-[#36454F]/70">{u.dispute_count || 0}</td>
                          <td className="py-3 px-4 text-sm font-['Manrope'] text-[#36454F]/70">{u.upload_count || 0}</td>
                          <td className="py-3 px-4 text-xs font-['Manrope'] text-[#36454F]/40">
                            {u.created_at ? new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Disputes Tab */}
          <TabsContent value="disputes">
            <div className="space-y-3 animate-fade-in-up">
              {disputes.length === 0 ? (
                <Card className="border-[#36454F]/15 bg-white">
                  <CardContent className="p-12 text-center">
                    <FileText className="w-12 h-12 text-[#36454F]/15 mx-auto mb-3" strokeWidth={1.5} />
                    <p className="text-[#36454F]/50 font-['Manrope']">No disputes yet</p>
                  </CardContent>
                </Card>
              ) : (
                disputes.map((d, i) => (
                  <Card key={d.dispute_id} className="border-[#36454F]/10 bg-white" data-testid={`admin-dispute-${i}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="font-['Manrope'] font-medium text-[#36454F] capitalize">{d.bureau}</span>
                            <StatusBadge status={d.status} />
                          </div>
                          <p className="text-xs text-[#36454F]/40 font-['Manrope'] mt-1">
                            ID: {d.dispute_id.slice(0, 8)} • 
                            Created {new Date(d.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            {d.mailed_at && ` • Mailed ${new Date(d.mailed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                          </p>
                        </div>
                        <span className="text-sm font-['Manrope'] text-[#36454F]/50">
                          {d.items?.length || 0} items
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Waitlist Tab */}
          <TabsContent value="waitlist">
            <div className="space-y-6 animate-fade-in-up" data-testid="waitlist-tab-content">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {waitlist && Object.entries(waitlist.by_brand).map(([brand, count]) => (
                  <Card key={brand} className="border-[#36454F]/15 bg-white" data-testid={`waitlist-count-${brand.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                    <CardContent className="p-5 text-center">
                      <p className="font-['Playfair_Display'] text-3xl font-semibold text-[#B76E79]">{count}</p>
                      <p className="text-xs uppercase tracking-wider text-[#36454F]/50 font-['Manrope'] mt-1">{brand}™</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
              {!waitlist || waitlist.signups.length === 0 ? (
                <Card className="border-[#36454F]/15 bg-white">
                  <CardContent className="p-12 text-center">
                    <Mail className="w-12 h-12 text-[#36454F]/15 mx-auto mb-3" strokeWidth={1.5} />
                    <p className="text-[#36454F]/50 font-['Manrope']">No waitlist signups yet</p>
                    <p className="text-xs text-[#36454F]/35 font-['Manrope'] mt-1">Signups from the "Be first to know" forms on the landing page will appear here.</p>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-[#36454F]/15 bg-white">
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-[#36454F]/10 bg-[#F5F5F5]">
                            <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-[#36454F]/50 font-['Manrope'] font-medium">Email</th>
                            <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-[#36454F]/50 font-['Manrope'] font-medium">Brand</th>
                            <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-[#36454F]/50 font-['Manrope'] font-medium">Signed up</th>
                          </tr>
                        </thead>
                        <tbody>
                          {waitlist.signups.map((s, i) => (
                            <tr key={i} className="border-b border-[#36454F]/5 hover:bg-[#F5F5F5]/50 transition-colors" data-testid={`waitlist-row-${i}`}>
                              <td className="py-3 px-4 text-sm font-['Manrope'] text-[#36454F] font-medium">{s.email}</td>
                              <td className="py-3 px-4">
                                <span className="text-xs px-2 py-0.5 rounded bg-[#B76E79]/10 text-[#B76E79] font-['Manrope']">{s.brand}™</span>
                              </td>
                              <td className="py-3 px-4 text-xs font-['Manrope'] text-[#36454F]/40">
                                {s.created_at ? new Date(s.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Ministry Tab */}
          <TabsContent value="ministry">
            <div className="space-y-6 animate-fade-in-up">
              <Card className="border-[#36454F]/15 bg-white">
                <CardContent className="p-8">
                  <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#36454F] mb-2">Ministry Dashboard</h2>
                  <p className="text-[#36454F]/65 font-['Manrope'] mb-6">
                    Share a public impact report with credit union and church ministry partners. Shows aggregate data only — no individual user information is exposed.
                  </p>

                  {ministryCode ? (
                    <div className="space-y-4">
                      <div className="p-4 bg-[#4CAF50]/10 border border-[#4CAF50]/30 rounded-sm">
                        <p className="text-xs text-[#4CAF50] font-['Manrope'] font-medium mb-2">Your shareable link is live:</p>
                        <div className="flex items-center gap-2">
                          <input
                            readOnly
                            value={ministryCode.url}
                            className="flex-1 bg-white border border-[#36454F]/15 rounded-sm px-3 py-2 text-sm font-['Manrope'] text-[#36454F]"
                            data-testid="ministry-url-input"
                          />
                          <button
                            onClick={() => { navigator.clipboard.writeText(ministryCode.url); toast.success('Link copied!'); }}
                            className="p-2 bg-white border border-[#36454F]/15 rounded-sm hover:bg-[#F5F5F5] transition-colors"
                            data-testid="copy-ministry-url"
                          >
                            <Copy className="w-4 h-4 text-[#36454F]/60" strokeWidth={1.5} />
                          </button>
                          <a
                            href={ministryCode.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-white border border-[#36454F]/15 rounded-sm hover:bg-[#F5F5F5] transition-colors"
                            data-testid="open-ministry-dashboard"
                          >
                            <ExternalLink className="w-4 h-4 text-[#36454F]/60" strokeWidth={1.5} />
                          </a>
                        </div>
                      </div>

                      <Button onClick={generateMinistryCode} variant="outline" className="border-[#B76E79] text-[#B76E79] hover:bg-[#B76E79] hover:text-white font-['Manrope'] rounded-sm" data-testid="regenerate-code-btn">
                        <Share2 className="w-4 h-4 mr-2" strokeWidth={1.5} />Regenerate Link
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Share2 className="w-12 h-12 text-[#36454F]/15 mx-auto mb-4" strokeWidth={1.5} />
                      <p className="text-[#36454F]/50 font-['Manrope'] mb-4">No ministry dashboard link has been created yet.</p>
                      <Button onClick={generateMinistryCode} disabled={generatingCode} className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm" data-testid="generate-code-btn">
                        {generatingCode ? 'Generating...' : <><Share2 className="w-4 h-4 mr-2" strokeWidth={1.5} />Create Ministry Dashboard Link</>}
                      </Button>
                    </div>
                  )}

                  <div className="mt-6 p-4 bg-[#F5F5F5] rounded-sm">
                    <h4 className="font-['Manrope'] font-semibold text-[#36454F] text-sm mb-2">What partners will see:</h4>
                    <ul className="space-y-1 text-xs text-[#36454F]/60 font-['Manrope']">
                      <li>Total members served, letters generated, disputes resolved</li>
                      <li>Total debt addressed and reports analyzed</li>
                      <li>Monthly activity trends</li>
                      <li>Approved user testimonies</li>
                      <li>Mission statement and "How It Works" walkthrough</li>
                      <li>Partnership contact information</li>
                    </ul>
                    <p className="mt-3 text-[10px] text-[#36454F]/40 font-['Manrope'] italic">
                      No individual user data, names, or financial details are ever exposed.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
      <AppFooter />
    </div>
  );
}

// Subcomponents
function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <Card className="border-[#36454F]/15 bg-white">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-[#36454F]/50 font-['Manrope'] uppercase tracking-wider">{label}</p>
            <p className="text-3xl font-['Manrope'] font-semibold text-[#36454F] mt-1">{value}</p>
            <p className="text-xs font-['Manrope'] mt-1" style={{ color }}>{sub}</p>
          </div>
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
            <Icon className="w-5 h-5" style={{ color }} strokeWidth={1.5} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function GrowthBar({ label, value, max }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div>
      <div className="flex justify-between text-xs font-['Manrope'] mb-1">
        <span className="text-[#36454F]/60">{label}</span>
        <span className="font-semibold text-[#36454F]">{value}</span>
      </div>
      <div className="h-2 bg-[#36454F]/5 rounded-sm overflow-hidden">
        <div className="h-full bg-[#B76E79] rounded-sm transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const config = {
    generated: { bg: 'bg-[#E0BFB8]/30', text: 'text-[#B76E79]', label: 'Generated' },
    mailed: { bg: 'bg-[#4CAF50]/10', text: 'text-[#4CAF50]', label: 'Mailed' },
    resolved: { bg: 'bg-[#4CAF50]/20', text: 'text-[#4CAF50]', label: 'Resolved' },
    response_received: { bg: 'bg-[#36454F]/10', text: 'text-[#36454F]', label: 'Response' },
    cfpb_escalated: { bg: 'bg-[#D9534F]/10', text: 'text-[#D9534F]', label: 'CFPB' }
  };
  const c = config[status] || config.generated;
  return <span className={`text-[10px] px-1.5 py-0.5 ${c.bg} ${c.text} font-['Manrope'] rounded`}>{c.label}</span>;
}
